import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Typography,
  Paper,
  IconButton,
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  Box,
  Snackbar,
  Divider,
  Card,
  CardContent,
  CardActions,
  Tooltip,
} from '@mui/material';
import {
  FilterList as FilterListIcon,
  ArrowBack as ArrowBackIcon,
  Receipt as ReceiptIcon,
  Warning as WarningIcon,
  MoneyOff as MoneyOffIcon,
  CheckCircle as CheckCircleIcon,
  AccountBalance as AccountBalanceIcon,
  Close as CloseIcon,
  Assessment as ReportIcon,
  CloudUpload as CloudUploadIcon,
  InsertDriveFile as InsertDriveFileIcon,
  ArrowForward as ArrowForwardIcon,
  GetApp as GetAppIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  SaveAs as SaveAsIcon,
} from '@mui/icons-material';
import { 
  DataGridPro, 
  GridToolbarContainer, 
  GridToolbarColumnsButton, 
  GridToolbarFilterButton, 
  GridToolbarDensitySelector,
  useGridApiContext,
  gridFilteredSortedRowEntriesSelector
} from '@mui/x-data-grid-pro';
import { format } from 'date-fns';
import { initializeFormats, formatDate, formatAmount, formatPrice, formatQuantity } from '../../../utils/global';
import { useFunctionPermissions } from '../../../hooks/useFunctionPermissions';
import { FC } from '../../../constants/functionCodes';
import * as XLSX from 'xlsx';
import { AuditLoggers } from '../../../utils/auditLogger';
import { harmonateTokens as ht } from '../../../theme/harmonateTokens';
import {
  harmonatePageSx,
  harmonatePageTitleSx,
  harmonateCardSx,
  harmonateDataGridSx,
  harmonateProceedButtonSx,
  harmonateCancelButtonSx,
  harmonateUploadOutlinedSx,
  harmonateAbortButtonSx,
  harmonateDialogPaperProps,
  harmonateDialogTitleSx,
  harmonateDialogContentSx,
  harmonateDialogFormContentSx,
  harmonateFormFieldsSx,
  harmonateTextFieldSx,
  harmonateInputLabelProps,
  harmonateSelectMenuProps,
  harmonateToolbarBuFieldSx,
  harmonateSectionTitleSx,
  harmonateGridActionIconSx,
} from '../../../theme/harmonateStyles';
import MerchantDataHubBreadcrumb from './MerchantDataHubBreadcrumb';
import { readMdhNavState, formatMdhPropertyLabel, applyPropertyScope, assignedPropertyCodes } from './merchantDataHubNav';

const getHarmonateStatusChipSx = (color) => {
  const base = {
    height: 24,
    fontSize: '0.75rem',
    fontWeight: 500,
    borderRadius: ht.buttonRadius,
    '& .MuiChip-label': { px: 1.5 },
  };
  switch (color) {
    case 'success':
      return { ...base, bgcolor: ht.secondaryTab, color: ht.text };
    case 'warning':
      return { ...base, bgcolor: ht.listBg, color: ht.text, border: `1px solid ${ht.line}` };
    case 'error':
      return { ...base, bgcolor: 'rgba(239, 63, 63, 0.12)', color: ht.red };
    case 'info':
      return { ...base, bgcolor: ht.listBg, color: ht.text, border: `1px solid ${ht.line}` };
    default:
      return { ...base, bgcolor: ht.listBg, color: ht.text };
  }
};

// Report types configuration
const REPORT_TYPES = [
  { 
    id: 'fraud', 
    label: 'Fraud Report',
    icon: <WarningIcon fontSize="small" />,
    description: 'View and analyze potential fraudulent transactions',
    hasSubReports: true,
    subReports: [
      {
        id: 'credits_exceed_debits',
        label: 'Credits that Exceed Debits',
        endpoint: 'credit-card-fraud/credits-exceed-debits',
        viewName: 'WORKFLOW.CREDIT_CARD_FRAUD_OUTPUT_CREDITS_THAT_EXCEED_DEBITS_VIEW',
        description: 'View credits that exceed corresponding debits'
      },
      {
        id: 'credits_without_debits',
        label: 'Credits without Debits',
        endpoint: 'credit-card-fraud/credits-without-debits',
        viewName: 'WORKFLOW.CREDIT_CARD_FRAUD_OUTPUT_CREDITS_WITHOUT_DEBITS_VIEW',
        description: 'View credits that have no corresponding debits'
      }
    ]
  },
  { 
    id: 'chargeback_detail', 
    label: 'Chargeback Detail',
    icon: <ReceiptIcon fontSize="small" />,
    description: 'Detailed view of all chargeback transactions',
    endpoint: 'credit-card-fraud/chargebacks/detail',
    viewName: 'WORKFLOW.CREDIT_CARD_FRAUD_OUTPUT_CHARGEBACKS_DETAIL_VIEW'
  },
  { 
    id: 'chargeback_win_loss', 
    label: 'Chargeback Win/Loss',
    icon: <MoneyOffIcon fontSize="small" />,
    description: 'Track chargeback success and failure rates',
    endpoint: 'credit-card-fraud/chargebacks/win-loss',
    viewName: 'WORKFLOW.CREDIT_CARD_FRAUD_OUTPUT_CHARGEBACKS_WIN_LOSS_VIEW'
  },
  { 
    id: 'daily_settlement', 
    label: 'Daily Settlement',
    icon: <AccountBalanceIcon fontSize="small" />,
    description: 'Daily settlement and reconciliation reports',
    endpoint: 'credit-card-fraud/daily-settlement',
    viewName: 'WORKFLOW.CREDIT_CARD_FRAUD_OUTPUT_DAILY_SETTLEMENT_VIEW'
  },
  { 
    id: 'forced_authorization', 
    label: 'Forced Authorization',
    icon: <CheckCircleIcon fontSize="small" />,
    description: 'View transactions that required manual authorization',
    endpoint: 'credit-card-fraud/forced-authorization',
    viewName: 'WORKFLOW.CREDIT_CARD_FRAUD_OUTPUT_FORCED_AUTHORIZATION_VIEW'
  },
];

const CreditCardTransactions = () => {
  const [searchParams] = useSearchParams();
  const mdhNav = useMemo(() => readMdhNavState(searchParams), [searchParams]);
  const [assignedProperties, setAssignedProperties] = useState([]);
  const [propertiesReady, setPropertiesReady] = useState(false);
  const userPropertyCodes = useMemo(
    () => assignedPropertyCodes(assignedProperties),
    [assignedProperties]
  );
  const gridRef = React.useRef();
  const [apiRef, setApiRef] = React.useState(null);
  const [transactions, setTransactions] = useState([]);
  
  // Use a ref to track if we've logged this page view in the current session
  const hasLoggedPageView = React.useRef(false);
  const loggingInProgress = React.useRef(false);
  
  useEffect(() => {
    // Only log if we haven't logged this page view yet and we're not currently logging
    if (!hasLoggedPageView.current && !loggingInProgress.current) {
      loggingInProgress.current = true;
      console.log('CreditCardTransactions component mounted, starting audit logging...');
      
      const logPageView = async () => {
        try {
          console.log('Starting audit log for credit card transactions page view');
          const userEmail = localStorage.getItem('username') || 'unknown';
          const tenantId = localStorage.getItem('tenant_id') || 'unknown';
          
          console.log('Calling AuditLoggers.creditCardViewTransactions with:', { userEmail, tenantId });
          
          const result = await AuditLoggers.creditCardViewTransactions({
            userId: userEmail,
            tenantId: tenantId,
            timestamp: new Date().toISOString()
          });
          
          console.log('Audit log result:', result);
          hasLoggedPageView.current = true;
        } catch (error) {
          console.error('Failed to log credit card transactions page view:', error);
        } finally {
          loggingInProgress.current = false;
        }
      };

      logPageView();
    }
    
    // Cleanup function to reset the flag if component unmounts
    return () => {
      // Optionally reset the flag when component unmounts if you want to log again on remount
      // hasLoggedPageView.current = false;
    };
  }, []);

  const [loading, setLoading] = useState(false);
  const initialReport = useMemo(() => {
    if (mdhNav.report) {
      const match = REPORT_TYPES.find((r) => r.id === mdhNav.report);
      if (match) return match;
    }
    return REPORT_TYPES[0];
  }, [mdhNav.report]);
  const [selectedReport, setSelectedReport] = useState(initialReport);
  const initialSubReport = useMemo(() => {
    if (initialReport.hasSubReports && initialReport.subReports) {
      if (mdhNav.subReport) {
        const match = initialReport.subReports.find((sr) => sr.id === mdhNav.subReport);
        if (match) return match.id;
      }
      return initialReport.subReports[0].id;
    }
    return null;
  }, [initialReport, mdhNav.subReport]);
  const [selectedSubReport, setSelectedSubReport] = useState(initialSubReport);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateRange, setDateRange] = useState({ start: null, end: null });
  const [rowSelectionModel, setRowSelectionModel] = useState([]);
  const [sortModel, setSortModel] = useState([]);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 100,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(1000);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [rowCountProp, setRowCountProp] = useState(0);
  const [filters, setFilters] = useState({
    merchant: [],
    status: [],
    transactionType: [],
    cardType: [],
    processor: [],
    region: [],
    authorizationStatus: []
  });
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState('');
  const [currentStep, setCurrentStep] = useState('');
  const [documentTypes, setDocumentTypes] = useState([]);
  const [selectedDocumentType, setSelectedDocumentType] = useState('');
  const [uploadedFile, setUploadedFile] = useState(null);
  const [loadingDocumentTypes, setLoadingDocumentTypes] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const [columns, setColumns] = useState([]); // State for grid configuration and formats
  const [gridConfig, setGridConfig] = useState(null);
  const [loadingConfig, setLoadingConfig] = useState(false);
  const [formatsInitialized, setFormatsInitialized] = useState(false);
  
  // Custom filter state
  const [customFilters, setCustomFilters] = useState([]);
  const [appliedFilters, setAppliedFilters] = useState([]); // Track currently applied filters
  const [showCustomFilters, setShowCustomFilters] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [saveFilterDialogOpen, setSaveFilterDialogOpen] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState('');
  const [filterName, setFilterName] = useState('');
  const [savingFilter, setSavingFilter] = useState(false);
  const [savedFilters, setSavedFilters] = useState([]);
  const [loadingFilters, setLoadingFilters] = useState(false);
  const [isFilterModified, setIsFilterModified] = useState(false);
  
  const { canUpload, canExport } = useFunctionPermissions(FC.MERCHANT_DATA_HUB);

  // Fetch saved filters for the current report
  const fetchSavedFilters = async () => {
    try {
      setLoadingFilters(true);
      const tenantId = localStorage.getItem('tenant_id');
      const reportName = selectedSubReport 
        ? `${selectedReport.id}-${selectedSubReport}` 
        : selectedReport.id;
      
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}conductor/credit-card-headers/${tenantId}/report/${reportName}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch saved filters');
      }

      const data = await response.json();
      setSavedFilters(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching saved filters:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load saved filters',
        severity: 'error'
      });
      setSavedFilters([]);
    } finally {
      setLoadingFilters(false);
    }
  };

  // State for delete confirmation dialog
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [filterToDelete, setFilterToDelete] = useState(null);

  // Handle delete confirmation dialog open
  const handleDeleteClick = (filterId) => {
    setFilterToDelete(filterId);
    setDeleteConfirmOpen(true);
  };
  
  // Handle opening the save filter dialog
  const handleOpenSaveDialog = () => {
    // If we have a selected filter, set its name
    if (selectedFilter && typeof selectedFilter === 'object') {
      setFilterName(selectedFilter.filterName || '');
    } else {
      setFilterName('');
    }
    setSaveFilterDialogOpen(true);
  };

  // Handle delete confirmation dialog close
  const handleDeleteClose = () => {
    setDeleteConfirmOpen(false);
    setFilterToDelete(null);
  };

  // Delete a saved filter
  const handleDeleteFilter = async () => {
    if (!filterToDelete) {
      setSnackbar({
        open: true,
        message: 'No filter selected for deletion',
        severity: 'error',
        autoHideDuration: 3000
      });
      handleDeleteClose();
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}conductor/credit-card-headers/${filterToDelete}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to delete filter');
      }

      // Refresh the filters list
      await fetchSavedFilters();
      
      // Clear selection if the deleted filter was selected
      if (selectedFilter && (selectedFilter.id === filterToDelete || selectedFilter === filterToDelete)) {
        setSelectedFilter('');
        clearCustomFilters();
      }

      setSnackbar({
        open: true,
        message: 'Filter deleted successfully',
        severity: 'success',
        autoHideDuration: 3000
      });
    } catch (error) {
      console.error('Error deleting filter:', error);
      setSnackbar({
        open: true,
        message: 'Failed to delete filter',
        severity: 'error',
        autoHideDuration: 3000
      });
    } finally {
      handleDeleteClose();
    }
  };

  // Apply a saved filter
  const applySavedFilter = (filterData) => {
    try {
      // If filterData is empty, clear the selection
      if (!filterData) {
        setSelectedFilter('');
        setFilterName('');
        return;
      }
      
      const filter = typeof filterData.filter === 'string' 
        ? JSON.parse(filterData.filter)
        : filterData.filter;
        
      // Set the selected filter and its name
      setSelectedFilter(filterData);
      setFilterName(filterData.filterName || '');
      
      // Convert date strings to Date objects if they exist
      const dateRange = filter.dateRange ? {
        start: filter.dateRange.start ? new Date(filter.dateRange.start) : null,
        end: filter.dateRange.end ? new Date(filter.dateRange.end) : null
      } : { start: null, end: null };
      
      // Apply basic filters
      setSearchTerm(filter.searchTerm || '');
      setStatusFilter(filter.statusFilter || 'all');
      setDateRange(dateRange);
      setFilters(filter.filters || {
        region: [],
        status: [],
        cardType: [],
        merchant: [],
        processor: [],
        transactionType: [],
        authorizationStatus: []
      });
      
      // Apply custom filters if they exist
      if (Array.isArray(filter.customFilters) && filter.customFilters.length > 0) {
        setCustomFilters(filter.customFilters);
        setShowCustomFilters(true);
      } else {
        setCustomFilters([]);
        // Only hide custom filters if there are no date filters either
        setShowCustomFilters(!!(dateRange.start || dateRange.end));
      }
      
      // Apply any applied filters
      setAppliedFilters(filter.customFilters || []);
      
      setSnackbar({
        open: true,
        message: `Filter "${filterData.filterName}" applied successfully`,
        severity: 'success'
      });
    } catch (error) {
      console.error('Error applying filter:', error);
      setSnackbar({
        open: true,
        message: 'Failed to apply filter. The filter format may be invalid.',
        severity: 'error'
      });
    }
  };

  // Save or update a filter
  const handleSaveFilter = async (isUpdate = false) => {
    if (!filterName.trim()) {
      setSnackbar({
        open: true,
        message: 'Please enter a name for the filter',
        severity: 'error'
      });
      return;
    }
    
    // If it's an update but no filter is selected, treat as new filter
    if (isUpdate && !selectedFilter) {
      isUpdate = false;
    }

    setSavingFilter(true);
    try {
      const tenantId = localStorage.getItem('tenant_id');
      const reportName = selectedSubReport 
        ? `${selectedReport.id}-${selectedSubReport}` 
        : selectedReport.id;
      
      // Process filters the same way as in handleLoadData
      const validFilters = customFilters.length > 0 
        ? customFilters.filter(f => f.column && f.operator && f.value)
        : appliedFilters;
      
      // Update applied filters if we have custom filters
      if (customFilters.length > 0) {
        setAppliedFilters(validFilters);
      }

      const filterData = {
        tenantId,
        report: reportName,
        filterName: filterName.trim(),
        filter: JSON.stringify({
          searchTerm,
          statusFilter,
          dateRange,
          filters,
          customFilters: validFilters,
          appliedFilters: validFilters // Also save the processed filters
        })
      };

      let url = `${process.env.REACT_APP_BACKEND_URL}conductor/credit-card-headers`;
      const method = isUpdate ? 'PUT' : 'POST';
      
      if (isUpdate && selectedFilter) {
        const filterId = typeof selectedFilter === 'string' ? selectedFilter : selectedFilter.id;
        if (!filterId) {
          throw new Error('Cannot update filter: No filter ID provided');
        }
        url = `${process.env.REACT_APP_BACKEND_URL}conductor/credit-card-headers/${filterId}`;
      }

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(filterData)
      });

      if (!response.ok) {
        throw new Error(`Failed to ${isUpdate ? 'update' : 'save'} filter`);
      }

      setSnackbar({
        open: true,
        message: `Filter ${isUpdate ? 'updated' : 'saved'} successfully`,
        severity: 'success'
      });
      
      setSaveFilterDialogOpen(false);
      setFilterName('');
      setSelectedFilter('');
      
      // Refresh the saved filters list
      await fetchSavedFilters();
    } catch (error) {
      console.error(`Error ${isUpdate ? 'updating' : 'saving'} filter:`, error);
      setSnackbar({
        open: true,
        message: `Failed to ${isUpdate ? 'update' : 'save'} filter: ${error.message}`,
        severity: 'error'
      });
    } finally {
      setSavingFilter(false);
    }
  };

  // Filter operators
  const FILTER_OPERATORS = [
    { value: '=', label: 'equals' },
    { value: '!=', label: 'not equals' },
    { value: '>', label: 'greater than' },
    { value: '>=', label: 'greater than or equal' },
    { value: '<', label: 'less than' },
    { value: '<=', label: 'less than or equal' },
    { value: 'contains', label: 'contains' },
    { value: 'startsWith', label: 'starts with' },
    { value: 'endsWith', label: 'ends with' },
    { value: 'isEmpty', label: 'is empty' },
    { value: 'isNotEmpty', label: 'is not empty' },
  ];

  // Get available columns for filtering
  const getAvailableColumns = () => {
    return columns
      .filter(col => col.filterable !== false)
      .map(col => ({
        value: col.field,
        label: col.headerName || col.field,
        type: col.type || 'string'
      }));
  };

  // Assigned properties from the Users screen. Empty selection is limited to these codes.
  useEffect(() => {
    const fetchAssignedProperties = async () => {
      try {
        const token = localStorage.getItem('authToken') || localStorage.getItem('token');
        const tenantId = localStorage.getItem('tenant_id');
        const userId = localStorage.getItem('user_id');
        if (!token || !tenantId || !userId) {
          setAssignedProperties([]);
          return;
        }
        const resp = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}conductor/user-tenant-properties?tenant_id=${encodeURIComponent(tenantId)}&user_id=${encodeURIComponent(userId)}`,
          {
            method: 'GET',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }
        );
        if (!resp.ok) {
          throw new Error(`Failed to fetch properties: ${resp.status}`);
        }
        const data = await resp.json();
        setAssignedProperties(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error('Error fetching assigned properties for credit card reports:', error);
        setAssignedProperties([]);
      } finally {
        setPropertiesReady(true);
      }
    };
    fetchAssignedProperties();
  }, []);

  // Initialize formats when component mounts
  useEffect(() => {
    const initFormats = async () => {
      const tenantId = localStorage.getItem('tenant_id');
      if (tenantId) {
        try {
          await initializeFormats(tenantId);
          setFormatsInitialized(true);
        } catch (error) {
          console.error('Error initializing formats:', error);
          setFormatsInitialized(true); // Continue with default formats
        }
      } else {
        console.warn('No tenant ID found, using default formats');
        setFormatsInitialized(true);
      }
    };

    initFormats();
  }, []);

  const propertyScopeKey = (mdhNav.propertyLocked && mdhNav.propertyCode)
    ? `one:${mdhNav.propertyCode}`
    : propertiesReady
      ? `assigned:${userPropertyCodes.join(',')}`
      : null;

  // Fetch report data when report or property scope changes
  useEffect(() => {
    if (formatsInitialized && propertyScopeKey) {
      setTotalCount(0);
      setRowCountProp(0);
      setHasMore(true);
      fetchReportData(selectedReport.id, selectedSubReport, currentPage, pageSize, dateRange.start, dateRange.end, appliedFilters);
      fetchSavedFilters();
    }
  }, [selectedReport.id, selectedSubReport, formatsInitialized, propertyScopeKey]);

  // Generate dynamic columns based on data
  const generateColumns = (data) => {
    if (!data || data.length === 0) return [];
    
    const firstRow = data[0];
    const generatedColumns = Object.keys(firstRow).map(key => {
      const isDate = key.toLowerCase().includes('date') || 
                     key.toLowerCase().includes('time') ||
                     (firstRow[key] && typeof firstRow[key] === 'string' && 
                      /^\d{4}-\d{2}-\d{2}/.test(firstRow[key]));
      
      const isNumber = typeof firstRow[key] === 'number' || 
                       key.toLowerCase().includes('amount') ||
                       key.toLowerCase().includes('price') ||
                       key.toLowerCase().includes('total');
      
      const isStatus = key.toLowerCase().includes('status');
      
      return {
        field: key,
        headerName: key.split('_').map(word => 
          word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        ).join(' '),
        flex: 1,
        minWidth: 150,
        type: isNumber ? 'number' : isDate ? 'date' : 'string',
        valueGetter: isDate ? (params) => {
          try {
            return params.value ? new Date(params.value) : null;
          } catch {
            return null;
          }
        } : undefined,
        renderCell: (params) => {
          if (!params.value) return '-';
          
          if (isDate) {
            try {
              return format(new Date(params.value), 'MMM dd, yyyy');
            } catch {
              return params.value;
            }
          }
          
          if (isNumber && typeof params.value === 'number') {
            return `$${params.value.toFixed(2)}`;
          }
          
          if (isStatus) {
            const statusColors = {
              completed: 'success',
              pending: 'warning',
              failed: 'error',
              won: 'success',
              lost: 'error',
              approved: 'success',
              declined: 'error',
            };
            
            const color = statusColors[params.value?.toLowerCase()] || 'default';
            
            return (
              <Chip 
                label={params.value} 
                size="small" 
                sx={getHarmonateStatusChipSx(color)}
              />
            );
          }
          
          return params.value;
        },
        filterable: true,
        sortable: true,
      };
    });
    
    return generatedColumns;
  };

  // Fetch grid configuration from the backend
  const fetchGridConfig = async (viewName) => {
    if (!viewName) return null;
    
    setLoadingConfig(true);
    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}conductor/config/default?category=${encodeURIComponent(viewName)}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch grid configuration: ${response.statusText}`);
      }

      const config = await response.json();
      return config;
    } catch (error) {
      console.error('Error fetching grid configuration:', error);
      return null;
    } finally {
      setLoadingConfig(false);
    }
  };

  // Generate columns from configuration using global formatters
  const generateColumnsFromConfig = (config) => {
    if (!config || !config.grid || !config.grid.columns) return [];
    
    return config.grid.columns.map(column => {
      const dataType = column.DataType?.toLowerCase() || 'string';
      const isDate = dataType === 'date' || dataType === 'datetime';
      const isNumber = dataType === 'number' || dataType === 'integer' || dataType === 'float';
      const isAmount = column.name.toLowerCase().includes('amount') || 
                      column.name.toLowerCase().includes('total') || 
                      column.name.toLowerCase().includes('price');
      const isQuantity = column.name.toLowerCase().includes('quantity') || 
                        column.name.toLowerCase().includes('qty') ||
                        column.name.toLowerCase().includes('count');
      
      return {
        field: column.name,
        headerName: column.title || column.name,
        type: isNumber ? 'number' : 'string',
        width: column.width || 150,
        flex: 1,
        minWidth: 150,
        valueGetter: (params) => {
          const value = params.value;
          if (value === null || value === undefined) return '';
          if (typeof value === 'object') return JSON.stringify(value);
          return value;
        },
        valueFormatter: (params) => {
          const value = params.value;
          if (value === null || value === undefined) return '';
          
          try {
            if (isDate) {
              return formatDate(value);
            } else if (isNumber) {
              const numValue = parseFloat(value);
              if (isNaN(numValue)) return value;
              
              // Use the appropriate formatting function based on column type and name
              if (isAmount) return formatAmount(numValue);
              if (isQuantity) return formatQuantity(numValue);
              return formatPrice(numValue);
            }
          } catch (e) {
            console.error(`Error formatting value for ${column.name}:`, e);
            return value; // Return original value on error
          }
          
          return String(value);
        },
        renderCell: (params) => {
          const value = params.value;
          if (value === null || value === undefined || value === '') return '-';
          
          // Special handling for status-like columns
          const statusColumns = ['status', 'state', 'chargeback_status', 'win_loss'];
          if (statusColumns.some(statusCol => column.name.toLowerCase().includes(statusCol))) {
            const statusColors = {
              completed: 'success',
              pending: 'warning',
              failed: 'error',
              won: 'success',
              lost: 'error',
              approved: 'success',
              declined: 'error',
              open: 'info',
              closed: 'default',
              in_progress: 'warning',
              resolved: 'success'
            };
            
            const color = statusColors[String(value).toLowerCase()] || 'default';
            
            return (
              <Chip 
                label={value} 
                size="small" 
                sx={getHarmonateStatusChipSx(color)}
              />
            );
          }
          
          // For date and number fields, use the formatted value from valueFormatter
          if (isDate || isNumber) {
            return params.formattedValue || value;
          }
          
          return String(value);
        }
      };
    });
  };

  // Fetch report data based on the selected report type
  const fetchReportData = async (reportId, subReportId = null, page = 1, ps = 1000, startDate = null, endDate = null, filters = []) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('token');
      const tenantIntegrationId = localStorage.getItem('tenant_integration');
      
      if (!token) {
        throw new Error('Authentication token not found');
      }

      if (!tenantIntegrationId) {
        throw new Error('Tenant integration ID not found');
      }
      
      const report = REPORT_TYPES.find(r => r.id === reportId);
      if (!report) {
        throw new Error('Invalid report type');
      }

      // Determine which endpoint and view to use
      let endpoint, viewName, reportLabel;
      
      if (report.hasSubReports && subReportId) {
        const subReport = report.subReports.find(sr => sr.id === subReportId);
        if (!subReport) {
          throw new Error('Invalid sub-report type');
        }
        endpoint = subReport.endpoint;
        viewName = subReport.viewName;
        reportLabel = `${report.label} - ${subReport.label}`;
      } else if (report.hasSubReports && !subReportId) {
        // If it's a report with sub-reports but none selected, use the first one
        const firstSubReport = report.subReports[0];
        endpoint = firstSubReport.endpoint;
        viewName = firstSubReport.viewName;
        reportLabel = `${report.label} - ${firstSubReport.label}`;
        setSelectedSubReport(firstSubReport.id);
      } else {
        endpoint = report.endpoint;
        viewName = report.viewName;
        reportLabel = report.label;
      }

      // Fetch grid configuration first
      const config = await fetchGridConfig(viewName);
      setGridConfig(config);
      
      // Generate columns from configuration if available
      if (config) {
        const newColumns = generateColumnsFromConfig(config);
        setColumns(newColumns);
      }

      // Build query parameters
      const params = new URLSearchParams({
        page: page,
        page_size: ps
      });

      // Add date range if provided
      if (startDate) {
        params.append('start_date', format(startDate, 'yyyy-MM-dd'));
      }
      if (endDate) {
        params.append('end_date', format(endDate, 'yyyy-MM-dd'));
      }

      // Add filters if provided
      if (filters && filters.length > 0) {
        // Process filters to remove value for operators that don't need it
        const processedFilters = filters.map(filter => {
          const { operator } = filter;
          // List of operators that don't require a value
          const noValueOperators = ['isEmpty', 'isNotEmpty'];
          
          if (noValueOperators.includes(operator)) {
            const { value, ...rest } = filter;
            return rest;
          }
          return filter;
        });
        
        params.append('filter', JSON.stringify(processedFilters));
      }

      const scope = applyPropertyScope(
        params,
        mdhNav.propertyLocked ? mdhNav.propertyCode : null,
        userPropertyCodes
      );

      // Fetch report data
      const apiUrl = `${process.env.REACT_APP_BACKEND_URL}fastapi/v1/${endpoint}/${tenantIntegrationId}?${params.toString()}`;
      console.log('Fetching report data:', { reportId, subReportId, apiUrl });

      const response = await fetch(apiUrl, {
        method: scope.method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-Tenant-ID': tenantIntegrationId
        },
        body: scope.body,
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to fetch ${reportLabel}: ${errorText}`);
      }

      const data = await response.json();
      console.log('Report data received:', data);
      
      // Handle different response structures
      let transactionData = [];
      
      if (data && data.data && Array.isArray(data.data)) {
        transactionData = data.data;
        setTotalCount(data.total || data.count || transactionData.length);
      } else if (Array.isArray(data)) {
        transactionData = data;
        setTotalCount(data.length);
      } else if (data && typeof data === 'object') {
        // If data is an object, convert it to an array of values
        if (data.data) {
          transactionData = Array.isArray(data.data) ? data.data : [data.data];
          setTotalCount(data.total || data.count || transactionData.length);
        } else {
          // If no data property, try to find an array in the response
          const arrayKey = Object.keys(data).find(key => Array.isArray(data[key]));
          if (arrayKey) {
            transactionData = data[arrayKey];
            setTotalCount(data.total || data.count || transactionData.length);
          } else {
            // If no array found, use the object itself as a single item
            transactionData = [data];
            setTotalCount(1);
          }
        }
      }
      
      // Add IDs if missing and ensure all fields from config are included
      const dataWithIds = transactionData.map((item, index) => {
        const newItem = { ...item };
        
        // Ensure all fields from config are present in the data
        if (config?.grid?.columns) {
          config.grid.columns.forEach(col => {
            if (newItem[col.name] === undefined) {
              newItem[col.name] = null; // or '' based on your preference
            }
          });
        }
        
        // Ensure we have an ID
        newItem.id = item.id || item._id || `row-${index}`;
        
        return newItem;
      });
      
      console.log('Processed transaction data:', dataWithIds);
      setTransactions(dataWithIds);
      
      // Set hasMore based on received count
      const receivedCount = transactionData.length;
      setHasMore(receivedCount === ps);
      
      // Set rowCountProp for DataGrid
      setRowCountProp((page - 1) * ps + receivedCount);
      
      // If no config was available, generate columns from data
      if (!config && dataWithIds.length > 0) {
        const newColumns = generateColumns(dataWithIds);
        setColumns(newColumns);
      }
      
      setSnackbar({
        open: true,
        message: `${reportLabel} loaded successfully`,
        severity: 'success',
      });
    } catch (error) {
      console.error('Error fetching report data:', error);
      setSnackbar({
        open: true,
        message: `Failed to load report: ${error.message}`,
        severity: 'error',
      });
      setTransactions([]);
      setColumns([]);
      setTotalCount(0);
      setHasMore(false);
      setRowCountProp(0);
    } finally {
      setLoading(false);
    }
  };

  // Reset filters when report type changes
  const resetFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setDateRange({ start: null, end: null });
    setFilters({
      merchant: [],
      status: [],
      transactionType: [],
      cardType: [],
      processor: [],
      region: [],
      authorizationStatus: []
    });
  };

  // Handle report type change
  const handleReportChange = (event) => {
    const reportId = event.target.value;
    const report = REPORT_TYPES.find(r => r.id === reportId);
    if (report) {
      setSelectedReport(report);
      setPaginationModel({ page: 0, pageSize: 100 });
      setCurrentPage(1);
      setPageSize(1000);
      setTotalCount(0);
      setRowCountProp(0);
      setHasMore(true);
      setSearchTerm('');
      setStatusFilter('all');
      setDateRange({ start: null, end: null });
      setCustomFilters([]);
      setAppliedFilters([]);
      setShowCustomFilters(false);
      resetFilters();
      
      // If report has sub-reports, select the first one by default
      if (report.hasSubReports && report.subReports.length > 0) {
        setSelectedSubReport(report.subReports[0].id);
      } else {
        setSelectedSubReport(null);
      }
    }
  };

  // Handle sub-report change
  const handleSubReportChange = (event) => {
    const subReportId = event.target.value;
    setSelectedSubReport(subReportId);
    setPaginationModel({ page: 0, pageSize: 100 });
    setCurrentPage(1);
    setPageSize(1000);
    setTotalCount(0);
    setRowCountProp(0);
    setHasMore(true);
    setSearchTerm('');
    setStatusFilter('all');
    setDateRange({ start: null, end: null });
    setCustomFilters([]);
    setAppliedFilters([]);
    setShowCustomFilters(false);
    resetFilters();
  };

  // Custom filter helper functions
  const addCustomFilter = () => {
    const newFilter = {
      id: Date.now(),
      column: '',
      operator: '=',
      value: ''
    };
    setCustomFilters([...customFilters, newFilter]);
    // Note: No API call is made here - filters are only applied when Apply Filter is clicked
  };

  const updateCustomFilter = (id, field, value) => {
    setCustomFilters(customFilters.map(filter => 
      filter.id === id ? { ...filter, [field]: value } : filter
    ));
    // Note: No API call is made here - filters are only applied when Apply Filter is clicked
  };

  const removeCustomFilter = (id) => {
    // Remove the filter from UI state
    const updatedFilters = customFilters.filter(filter => filter.id !== id);
    setCustomFilters(updatedFilters);
    
    // Remove from applied filters if it exists there
    if (appliedFilters.some(filter => filter.id === id)) {
      const updatedAppliedFilters = appliedFilters.filter(filter => filter.id !== id);
      setAppliedFilters(updatedAppliedFilters);
      
      // Update the selectedFilter's customFilters if a filter is selected
      if (selectedFilter && selectedFilter.filter) {
        const filter = typeof selectedFilter.filter === 'string' 
          ? JSON.parse(selectedFilter.filter)
          : selectedFilter.filter;
          
        if (filter.customFilters && filter.customFilters.some(f => f.id === id)) {
          const updatedSelectedFilter = {
            ...selectedFilter,
            filter: {
              ...filter,
              customFilters: filter.customFilters.filter(f => f.id !== id)
            }
          };
          setSelectedFilter(updatedSelectedFilter);
        }
      }
    }
  };

  // This function is no longer needed as we're using handleLoadData instead
  // The functionality has been merged into handleLoadData


  const clearCustomFilters = (e) => {
    // Prevent default form submission behavior and stop event propagation
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    // Clear the custom filters from the dialog
    setCustomFilters([]);
    setAppliedFilters([]);
    
    // Clear the selected filter
    setSelectedFilter('');
    
    // Reset the date range in the filter dialog
    setDateRange({ start: null, end: null });
    
    // Note: We're not making any API calls or resetting pagination here
    // as per the requirement to just clear the filter dialog
  };

  // Handle download report as CSV
  const handleDownloadReport = async () => {
    if (!canExport) return;
    setDownloading(true);
    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('token');
      const tenantIntegrationId = localStorage.getItem('tenant_integration');
      
      if (!token) {
        throw new Error('Authentication token not found');
      }

      if (!tenantIntegrationId) {
        throw new Error('Tenant integration ID not found');
      }
      
      const report = REPORT_TYPES.find(r => r.id === selectedReport.id);
      if (!report) {
        throw new Error('Invalid report type');
      }

      // Determine which endpoint to use
      let endpoint, viewName, reportLabel;
      
      if (report.hasSubReports && selectedSubReport) {
        const subReport = report.subReports.find(sr => sr.id === selectedSubReport);
        if (!subReport) {
          throw new Error('Invalid sub-report type');
        }
        endpoint = subReport.endpoint;
        viewName = subReport.viewName;
        reportLabel = `${report.label} - ${subReport.label}`;
      } else if (report.hasSubReports && !selectedSubReport) {
        // If it's a report with sub-reports but none selected, use the first one
        const firstSubReport = report.subReports[0];
        endpoint = firstSubReport.endpoint;
        viewName = firstSubReport.viewName;
        reportLabel = `${report.label} - ${firstSubReport.label}`;
      } else {
        endpoint = report.endpoint;
        viewName = report.viewName;
        reportLabel = report.label;
      }

      // Build query parameters for CSV download
      const params = new URLSearchParams({
        format: 'csv'
      });

      // Add date range if provided
      if (dateRange.start) {
        params.append('start_date', format(dateRange.start, 'yyyy-MM-dd'));
      }
      if (dateRange.end) {
        params.append('end_date', format(dateRange.end, 'yyyy-MM-dd'));
      }

      // Add filters if provided
      if (appliedFilters && appliedFilters.length > 0) {
        params.append('filter', JSON.stringify(appliedFilters));
      }

      const scope = applyPropertyScope(
        params,
        mdhNav.propertyLocked ? mdhNav.propertyCode : null,
        userPropertyCodes
      );

      // Construct the download URL
      const apiUrl = `${process.env.REACT_APP_BACKEND_URL}fastapi/v1/${endpoint}/${tenantIntegrationId}?${params.toString()}`;
      console.log('Downloading report:', { reportId: selectedReport.id, subReportId: selectedSubReport, apiUrl });

      // Make the download request
      const response = await fetch(apiUrl, {
        method: scope.method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-Tenant-ID': tenantIntegrationId
        },
        body: scope.body,
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to download report: ${errorText}`);
      }

      // Get the CSV data
      const csvData = await response.text();
      
      // Create and trigger download
      const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      
      // Generate filename with timestamp
      const timestamp = new Date().toISOString().split('T')[0];
      const filename = `${reportLabel.replace(/[^a-z0-9]/gi, '_')}_${timestamp}.csv`;
      link.setAttribute('download', filename);
      
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      setSnackbar({
        open: true,
        message: `${reportLabel} downloaded successfully`,
        severity: 'success',
      });
      
    } catch (error) {
      console.error('Error downloading report:', error);
      setSnackbar({
        open: true,
        message: `Failed to download report: ${error.message}`,
        severity: 'error',
      });
    } finally {
      setDownloading(false);
    }
  };

  // Handle Excel export
  const exportToCsv = (data, filename) => {
    // Extract headers from columns
    const headers = columns.map(col => `"${col.headerName || col.field}"`).join(',');
    
    // Convert data to CSV rows
    const rows = data.map(row => 
      columns.map(col => {
        const value = row[col.field];
        // Handle potential issues with values containing commas or quotes
        const escaped = ('' + (value || '')).replace(/"/g, '""');
        return `"${escaped}"`;
      }).join(',')
    );
    
    // Combine headers and rows
    const csvContent = [headers, ...rows].join('\n');
    
    // Create download link
    const blob = new Blob(["\uFEFF" + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `${filename}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleExcelExport = () => {
    // Prepare data for export
    const exportData = filteredTransactions.map(row => {
      const exportRow = {};
      columns.forEach(col => {
        const value = row[col.field];
        
        // Format the value based on column type
        if (value === null || value === undefined) {
          exportRow[col.headerName] = '';
        } else if (col.type === 'date' || col.field.toLowerCase().includes('date')) {
          try {
            exportRow[col.headerName] = format(new Date(value), 'MMM dd, yyyy');
          } catch {
            exportRow[col.headerName] = value;
          }
        } else if (col.type === 'number' || typeof value === 'number') {
          exportRow[col.headerName] = value;
        } else {
          exportRow[col.headerName] = value;
        }
      });
      return exportRow;
    });

    // Create worksheet
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    
    // Set column widths
    const columnWidths = columns.map(col => ({
      wch: Math.max(col.headerName.length, 15)
    }));
    worksheet['!cols'] = columnWidths;

    // Create workbook
    const workbook = XLSX.utils.book_new();
    const sheetName = selectedReport.hasSubReports && selectedSubReport
      ? `${selectedReport.label} - ${selectedReport.subReports.find(sr => sr.id === selectedSubReport)?.label || ''}`
      : selectedReport.label;
    
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName.substring(0, 31)); // Excel sheet name limit is 31 chars

    // Generate filename
    const fileName = `${sheetName.replace(/[^a-z0-9]/gi, '_')}_${new Date().toISOString().split('T')[0]}.xlsx`;

    // Download file
    XLSX.writeFile(workbook, fileName);
    
    setSnackbar({
      open: true,
      message: 'Excel file downloaded successfully',
      severity: 'success',
    });
  };

  // Initial data fetch
  useEffect(() => {
    fetchDocumentTypes();
  }, []);

  // Apply filters to transactions (removed client-side date filtering since handled server-side)
  const filteredTransactions = React.useMemo(() => {
    return transactions.filter(transaction => {
      // Basic search
      const matchesSearch = !searchTerm || 
        Object.values(transaction).some(value => 
          String(value).toLowerCase().includes(searchTerm.toLowerCase())
        );
      
      // Status filter
      const matchesStatus = statusFilter === 'all' || 
        (transaction.status && transaction.status.toLowerCase() === statusFilter.toLowerCase());
      
      // Report-specific filters
      let matchesReportFilters = true;
      
      // Common filters
      if (filters.merchant.length > 0 && transaction.merchant) {
        matchesReportFilters = matchesReportFilters && filters.merchant.includes(transaction.merchant);
      }
      
      if (filters.status.length > 0 && transaction.status) {
        matchesReportFilters = matchesReportFilters && filters.status.includes(transaction.status);
      }
      
      // Report type specific filters
      switch(selectedReport.id) {
        case 'fraud':
          if (filters.transactionType.length > 0 && transaction.transactionType) {
            matchesReportFilters = matchesReportFilters && 
              filters.transactionType.includes(transaction.transactionType);
          }
          break;
          
        case 'chargeback_detail':
        case 'chargeback_win_loss':
          if (filters.cardType.length > 0 && transaction.cardType) {
            matchesReportFilters = matchesReportFilters && 
              filters.cardType.includes(transaction.cardType);
          }
          break;
          
        case 'daily_settlement':
          if (filters.processor.length > 0 && transaction.processor) {
            matchesReportFilters = matchesReportFilters && 
              filters.processor.includes(transaction.processor);
          }
          if (filters.region.length > 0 && transaction.region) {
            matchesReportFilters = matchesReportFilters && 
              filters.region.includes(transaction.region);
          }
          if (filters.status.length > 0 && transaction.status) {
            matchesReportFilters = matchesReportFilters && 
              filters.status.includes(transaction.status);
          }
          break;
          
        case 'forced_authorization':
          if (filters.authorizationStatus.length > 0 && transaction.authorizationStatus) {
            matchesReportFilters = matchesReportFilters && 
              filters.authorizationStatus.includes(transaction.authorizationStatus);
          }
          break;
          
        default:
          break;
      }
      
      return matchesSearch && matchesStatus && matchesReportFilters;
    });
  }, [transactions, searchTerm, statusFilter, filters, selectedReport.id]);

  // // Fetch document types from API
  // const fetchDocumentTypes = async () => {
  //   setLoadingDocumentTypes(true);
  //   try {
  //     const token = localStorage.getItem('authToken') || localStorage.getItem('token');
  //     const category = 'CC';
  //     const tenantId = localStorage.getItem('tenant_id');
      
  //     const response = await fetch(
  //       `${process.env.REACT_APP_BACKEND_URL}conductor/document-types/by-category/${category}`,
  //       {
  //         headers: {
  //           'Authorization': `Bearer ${token}`,
  //           'Content-Type': 'application/json'
  //         }
  //       }
  //     );
      
  //     if (!response.ok) {
  //       throw new Error('Failed to fetch document types');
  //     }
      
  //     const data = await response.json();
  //     const filteredData = data.filter(type => type.name !== 'Unidentified');
  //     setDocumentTypes(filteredData);
  //     if (filteredData.length > 0) {
  //       setSelectedDocumentType(filteredData[0].id);
  //     }
  //   } catch (error) {
  //     console.error('Error fetching document types:', error);
  //     setUploadError('Failed to load document types');
  //   } finally {
  //     setLoadingDocumentTypes(false);
  //   }
  // };

  // Fetch document types from API
  const fetchDocumentTypes = async () => {
    setLoadingDocumentTypes(true);
    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('token');
      const category = 'CC';
      const tenantId = localStorage.getItem('tenant_id');
      
      if (!tenantId) {
        throw new Error('Tenant ID not found');
      }
      
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}conductor/document-type-tenant-configs/tenant/${tenantId}?category=${category}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (!response.ok) {
        throw new Error('Failed to fetch document types');
      }
      
      const data = await response.json();
      // Map the new response structure to match the expected format
      const mappedData = data.map(item => ({
        id: item.documentTypeId,
        name: item.documentTypeName,
        tenantConfigId: item.id, // Store the config ID for future use if needed
        includeTerms: item.includeTerms,
        excludeTerms: item.excludeTerms
      }));
      
      // Filter out any unwanted document types
      const filteredData = mappedData.filter(type => type.name !== 'Unidentified');
      setDocumentTypes(filteredData);
      if (filteredData.length > 0) {
        setSelectedDocumentType(filteredData[0].id);
      }
    } catch (error) {
      console.error('Error fetching document types:', error);
      setUploadError('Failed to load document types');
    } finally {
      setLoadingDocumentTypes(false);
    }
  };

  // Handle file upload
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    if (!file.name.match(/\.(csv|xlsx?)$/i)) {
      setUploadError('Please upload a valid file (.csv, .xls, or .xlsx)');
      return;
    }
    
    setUploadedFile(file);
    setUploadError('');
  };

  // API base URL
  const API_BASE = process.env.REACT_APP_API_URL || '';
  
  // Get auth token helper function
  const getAuthToken = () => {
    return localStorage.getItem('authToken') || localStorage.getItem('token');
  };

  // Complete the multipart upload
  const completeMultipartUpload = async (fileMeta, parts) => {
    const token = getAuthToken();
    const response = await fetch(`${API_BASE}/multipart/complete`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        id: fileMeta.id,
        unit_of_work_id: fileMeta.unit_of_work_id,
        location: fileMeta.location,
        upload_id: fileMeta.upload_id,
        name: fileMeta.name,
        content_type: fileMeta.content_type,
        parts: parts
      })
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to complete upload: ${response.status} ${response.statusText} - ${errorText}`);
    }

    return await response.json();
  };

  // Get presigned URLs for each part
  const getPresignedUrls = async (file, unitOfWorkId, fileMeta) => {
    setCurrentStep('Preparing upload...');
    const totalParts = Math.ceil(file.size / (5 * 1024 * 1024)); // 5MB chunks

    const token = getAuthToken();
    const tenantId = localStorage.getItem('tenant_id');
    const apiUrl = `${API_BASE}/multipart/urls`;

    const payload = {
      id: fileMeta.id,
      tenant_id: tenantId,
      unit_of_work_id: unitOfWorkId,
      location: fileMeta.location,
      upload_id: fileMeta.upload_id,
      name: file.name,
      content_type: file.type || 'application/octet-stream',
      total_parts: totalParts
    };

    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'Accept': 'application/json'
      },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to get upload URLs: ${errorText}`);
    }

    return await response.json();
  };

  // Upload a file part to S3
  const uploadPart = async (url, partData) => {
    const response = await fetch(url, {
      method: 'PUT',
      body: partData,
      headers: {
        'Content-Type': 'application/octet-stream'
      }
    });

    if (!response.ok) {
      throw new Error(`Failed to upload part: ${response.status} ${response.statusText}`);
    }

    // Get the ETag from the response headers
    const eTag = response.headers.get('ETag');
    if (!eTag) {
      throw new Error('No ETag in upload response');
    }

    // Remove surrounding quotes from ETag if present
    return eTag.replace(/^"|"$/g, '');
  };

  // Handle upload button click
  const handleUpload = async () => {
    try {
      await handleFileUploadToServer();
    } catch (error) {
      console.error('Error during file upload:', error);
      setSnackbar({
        open: true,
        message: error.message || 'Failed to upload file',
        severity: 'error',
      });
    }
  };

  // Handle file upload to server
  const handleFileUploadToServer = async () => {
    if (!uploadedFile || !selectedDocumentType) {
      setUploadError('Please select a file and document type');
      return;
    }
    
    setIsUploading(true);
    setUploadProgress(0);
    setUploadError('');
    setCurrentStep('Preparing upload...');
    
    const token = getAuthToken();
    const tenantId = localStorage.getItem('tenant_id');
    const userId = localStorage.getItem('user_id');
    
    try {
      // 1. First API: Create a unit of work
      setCurrentStep('Creating upload session...');
      // Get the selected document type to determine source_general_ledger
      const selectedDocType = documentTypes.find(doc => doc.id === selectedDocumentType);
      const sourceGeneralLedger = selectedDocType?.name.endsWith('Chargebacks') 
        ? 'CHARGEBACKS' 
        : 'TRANSACTIONS';

      const payload = {
        name: `Credit Card Import - ${new Date().toISOString()}`,
        documents: [{
          name: uploadedFile.name,
          content_type: uploadedFile.type || 'application/octet-stream',
          document_type_id: selectedDocumentType,
          source_general_ledger: sourceGeneralLedger,
          // system_id: 'CREDIT_CARD',
          fund_id: null,
          investor_id: null,
          holding_id: null
        }],
        tenant_id: tenantId,
        created_by: userId
      };
      
      const baseUrl = API_BASE.endsWith('/') ? API_BASE.slice(0, -1) : API_BASE;
      const apiUrl = `${baseUrl}/${tenantId}/units-of-work`;
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to create upload session: ${errorText}`);
      }
      
      const unitOfWork = await response.json();
      const document = unitOfWork.documents?.[0];
      
      if (!document) {
        throw new Error('No document in unit of work response');
      }

      // 2. Second API: Get presigned URLs for file parts
      setCurrentStep('Preparing file upload...');
      const presignedUrlsResponse = await getPresignedUrls(uploadedFile, unitOfWork.id, {
        id: document.id,
        location: document.location,
        upload_id: document.upload_id,
        name: document.name,
        content_type: document.content_type
      });
      
      // Make sure we have the expected response structure
      if (!presignedUrlsResponse || !presignedUrlsResponse.urls || !Array.isArray(presignedUrlsResponse.urls)) {
        throw new Error('Invalid response from presigned URLs API');
      }

      // 3. Upload file parts in parallel
      setCurrentStep('Uploading file...');
      const chunkSize = 5 * 1024 * 1024; // 5MB chunks
      const totalChunks = Math.ceil(uploadedFile.size / chunkSize);
      const parts = [];

      for (let partNumber = 1; partNumber <= totalChunks; partNumber++) {
        const start = (partNumber - 1) * chunkSize;
        const end = Math.min(start + chunkSize, uploadedFile.size);
        const chunk = uploadedFile.slice(start, end);
        
        // Find the URL for this part number
        const urlObj = presignedUrlsResponse.urls.find(p => p.part_number === partNumber);
        if (!urlObj || !urlObj.url || typeof urlObj.url !== 'string') {
          throw new Error(`No valid URL found for part ${partNumber}`);
        }
        
        setCurrentStep(`Uploading part ${partNumber} of ${totalChunks}...`);
        const eTag = await uploadPart(urlObj.url, chunk);
        
        parts.push({
          part_number: partNumber,
          etag: eTag
        });
        
        const progress = Math.round((partNumber / totalChunks) * 100);
        setUploadProgress(progress);
      }

      // 4. Complete the multipart upload
      setCurrentStep('Finalizing upload...');
      await completeMultipartUpload({
        id: document.id,
        unit_of_work_id: unitOfWork.id,
        location: document.location,
        upload_id: document.upload_id,
        name: document.name,
        content_type: document.content_type
      }, parts);
      
      setUploadProgress(100);
      setCurrentStep('Processing complete');
      
      setSnackbar({
        open: true,
        message: 'File uploaded successfully',
        severity: 'success',
      });
      
      setUploadedFile(null);
      setUploadDialogOpen(false);
      
      // Refresh data
      fetchReportData(selectedReport.id, selectedSubReport, currentPage, pageSize, dateRange.start, dateRange.end, appliedFilters);
      
    } catch (error) {
      console.error('Error uploading file:', error);
      setUploadError(error.message || 'Failed to upload file');
      setSnackbar({
        open: true,
        message: error.message || 'Failed to upload file',
        severity: 'error',
      });
    } finally {
      setIsUploading(false);
      setCurrentStep('');
    }
  };

  const handleLoadData = (e) => {
    // Prevent default form submission behavior if called from a form
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    // Filter out empty/incomplete filters if we have custom filters
    const validFilters = customFilters.length > 0 
      ? customFilters.filter(f => f.column && f.operator && f.value)
      : appliedFilters;
    
    // Update applied filters if we have custom filters
    if (customFilters.length > 0) {
      setAppliedFilters(validFilters);
    }
    
    // Reset pagination state
    setCurrentPage(1);
    setPageSize(1000);
    setTotalCount(0);
    setRowCountProp(0);
    setHasMore(true);
    
    // Fetch data with the current date range and filters
    fetchReportData(
      selectedReport.id, 
      selectedSubReport, 
      1, 
      1000, 
      dateRange.start, 
      dateRange.end, 
      validFilters
    );
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      const newPage = currentPage - 1;
      setCurrentPage(newPage);
      fetchReportData(selectedReport.id, selectedSubReport, newPage, pageSize, dateRange.start, dateRange.end, appliedFilters);
    }
  };

  const handleNextPage = () => {
    if (hasMore) {
      const newPage = currentPage + 1;
      setCurrentPage(newPage);
      fetchReportData(selectedReport.id, selectedSubReport, newPage, pageSize, dateRange.start, dateRange.end, appliedFilters);
    }
  };

  return (
    <>
      <Box sx={{ ...harmonatePageSx, height: '100vh' }}>
        <Box display="flex" flexDirection="column" sx={{ flex: 1, minHeight: 0 }}>
          {mdhNav.fromHub && (
            <MerchantDataHubBreadcrumb
              currentLabel="Credit Card Reports"
              propertyLabel={
                mdhNav.propertyLocked
                  ? formatMdhPropertyLabel(mdhNav.propertyCode, mdhNav.propertyName)
                  : null
              }
            />
          )}
          <Box display="flex" alignItems="center" mb={2} flexWrap="wrap" gap={2} flexShrink={0}>
            <Typography variant="h6" sx={harmonatePageTitleSx}>
              Credit Card Reports
            </Typography>
            
            <FormControl sx={{ ...harmonateToolbarBuFieldSx, minWidth: 250 }} size="small">
              <InputLabel id="report-selector-label">Select Report</InputLabel>
              <Select
                labelId="report-selector-label"
                id="report-selector"
                value={selectedReport.id}
                label="Select Report"
                onChange={handleReportChange}
                MenuProps={harmonateSelectMenuProps}
              >
                {REPORT_TYPES.map((report) => (
                  <MenuItem key={report.id} value={report.id}>
                    <Box display="flex" alignItems="center" gap={1}>
                      {report.icon}
                      {report.label}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            {/* Sub-report selector - only show for reports with sub-reports */}
            {selectedReport.hasSubReports && selectedReport.subReports && (
              <FormControl sx={{ ...harmonateToolbarBuFieldSx, minWidth: 250 }} size="small">
                <InputLabel id="sub-report-selector-label">Select Sub-Report</InputLabel>
                <Select
                  labelId="sub-report-selector-label"
                  id="sub-report-selector"
                  value={selectedSubReport || ''}
                  label="Select Sub-Report"
                  onChange={handleSubReportChange}
                  MenuProps={harmonateSelectMenuProps}
                >
                  {selectedReport.subReports.map((subReport) => (
                    <MenuItem key={subReport.id} value={subReport.id}>
                      {subReport.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            
            <Box ml="auto" display="flex" gap={2}>
              <Tooltip title={!canUpload ? "You do not have permission to upload statements" : "Upload credit card statement"}>
                <span>
              <Button
                variant="contained"
                startIcon={<CloudUploadIcon />}
                onClick={() => {
                  if (!canUpload) return;
                  setUploadDialogOpen(true);
                }}
                    disabled={!canUpload}
                    sx={harmonateProceedButtonSx}
              >
                Upload Statement
              </Button>
                </span>
              </Tooltip>
            </Box>
          </Box>
          
          <Box display="flex" justifyContent="flex-start" alignItems="center" mb={2} flexShrink={0}>
            <Typography variant="subtitle1" sx={{ color: ht.white }}>
              {selectedReport.hasSubReports && selectedSubReport
                ? selectedReport.subReports.find(sr => sr.id === selectedSubReport)?.description || selectedReport.description
                : selectedReport.description}
            </Typography>
          </Box>
          
          <Paper elevation={0} sx={{ ...harmonateCardSx, bgcolor: ht.white, p: 2, mb: 2, flexShrink: 0 }}>
            <Box display="flex" justifyContent="flex-end" alignItems="center" mb={2}>
              <Box display="flex" alignItems="center" gap={1}>
                <Tooltip title={!canExport ? "You do not have permission to export reports" : "Download report as CSV"}>
                  <span>
                <Button
                  variant="outlined"
                  onClick={handleDownloadReport}
                      disabled={downloading || !canExport}
                  startIcon={downloading ? <CircularProgress size={20} color="inherit" /> : <GetAppIcon />}
                      sx={harmonateUploadOutlinedSx}
                >
                  {downloading ? 'Downloading...' : 'Download'}
                </Button>
                  </span>
                </Tooltip>
                
                <Button
                  variant={showCustomFilters ? 'contained' : 'outlined'}
                  onClick={() => setShowCustomFilters(!showCustomFilters)}
                  startIcon={<FilterListIcon />}
                  sx={showCustomFilters ? harmonateProceedButtonSx : harmonateUploadOutlinedSx}
                >
                  {showCustomFilters ? 'Hide Filters' : 'Show Filters'}
                </Button>
                
              </Box>
            </Box>
            
          </Paper>

          {/* Custom Filter Interface */}
          {showCustomFilters && (
            <Paper elevation={0} sx={{ ...harmonateCardSx, bgcolor: ht.white, p: 2, mb: 2, border: `1px solid ${ht.line}`, flexShrink: 0 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6" sx={harmonateSectionTitleSx}>
                  Custom Filters
                </Typography>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} gap={1} flexWrap="wrap">
                  {/* Left side - Add/Apply/Clear Filters */}
                  <Box display="flex" gap={1} alignItems="center">
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<FilterListIcon />}
                      onClick={addCustomFilter}
                      sx={harmonateUploadOutlinedSx}
                    >
                      Add Filter
                    </Button>
                    
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={clearCustomFilters}
                      sx={harmonateCancelButtonSx}
                    >
                      Clear Filter
                    </Button>
                  </Box>

                  {/* Right side - Save/Load Filters */}
                  <Box display="flex" gap={1} alignItems="center" sx={harmonateFormFieldsSx}>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={handleOpenSaveDialog}
                      startIcon={<SaveIcon />}
                      disabled={customFilters.length === 0 && !(dateRange.start || dateRange.end)}
                      sx={{
                        ...harmonateUploadOutlinedSx,
                        '& .MuiButton-startIcon': {
                          marginRight: '4px',
                          marginLeft: '-4px'
                        }
                      }}
                    >
                      Save Filter
                    </Button>
                    
                    <FormControl size="small" sx={{ minWidth: 180, ...harmonateTextFieldSx }}>
                      <InputLabel>Select a filter</InputLabel>
                      <Select
                        value={selectedFilter || ''}
                        onChange={(e) => {
                          if (!e.target.value) {
                            applySavedFilter(null);
                          } else {
                            // Find the full filter object to pass to applySavedFilter
                            const selected = savedFilters.find(f => f.id === e.target.value);
                            applySavedFilter(selected);
                          }
                        }}
                        disabled={loadingFilters || savedFilters.length === 0}
                        displayEmpty
                        size="small"
                        MenuProps={harmonateSelectMenuProps}
                        renderValue={(selected) => {
                          if (!selected) return '';
                          const filter = typeof selected === 'object' ? selected : savedFilters.find(f => f.id === selected);
                          return filter?.filterName || '';
                        }}
                      >
                        {loadingFilters ? (
                          <MenuItem value="" disabled>
                            Loading...
                          </MenuItem>
                        ) : (
                          <MenuItem value="">
                            <em>Select a filter</em>
                          </MenuItem>
                        )}
                        {savedFilters.map((filter) => (
                          <MenuItem 
                            key={filter.id} 
                            value={filter.id}
                          >
                            {filter.filterName}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Tooltip title="Delete selected filter">
                      <IconButton 
                        size="small" 
                        onClick={() => {
                          const filterId = selectedFilter?.id || selectedFilter;
                          if (filterId) {
                            handleDeleteClick(filterId);
                          }
                        }}
                        disabled={!selectedFilter || loadingFilters || savedFilters.length === 0}
                        sx={{ 
                          ...harmonateGridActionIconSx,
                          '&:hover': { color: ht.red, bgcolor: 'rgba(239, 63, 63, 0.08)' },
                          visibility: selectedFilter ? 'visible' : 'hidden',
                          marginLeft: '-8px'
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>  
                    {/* Delete Confirmation Dialog */}
                    <Dialog
                      open={deleteConfirmOpen}
                      onClose={handleDeleteClose}
                      aria-labelledby="alert-dialog-title"
                      aria-describedby="alert-dialog-description"
                      PaperProps={harmonateDialogPaperProps}
                    >
                      <DialogTitle id="alert-dialog-title" sx={harmonateDialogTitleSx}>
                        Delete Filter
                      </DialogTitle>
                      <DialogContent sx={harmonateDialogContentSx}>
                        <DialogContentText id="alert-dialog-description" sx={{ color: ht.text }}>
                          Are you sure you want to delete this filter? This action cannot be undone.
                        </DialogContentText>
                      </DialogContent>
                      <DialogActions sx={{ px: 3, py: 2 }}>
                        <Button onClick={handleDeleteClose} variant="outlined" sx={harmonateCancelButtonSx}>
                          No, Keep It
                        </Button>
                        <Button onClick={handleDeleteFilter} autoFocus sx={harmonateAbortButtonSx}>
                          Yes, Delete It
                        </Button>
                      </DialogActions>
                    </Dialog>
                  </Box>
                </Box>
              </Box>

              {/* Date Range Picker */}
              <Box mb={3} sx={harmonateFormFieldsSx}>
                <Box display="flex" gap={1} alignItems="center">
                  <TextField
                    size="small"
                    label="From"
                    type="date"
                    value={dateRange.start?.toISOString().split('T')[0] || ''}
                    onChange={(e) => setDateRange(prev => ({
                      ...prev,
                      start: e.target.value ? new Date(e.target.value) : null
                    }))}
                    InputLabelProps={{ shrink: true, ...harmonateInputLabelProps }}
                    sx={{ width: '160px', ...harmonateTextFieldSx }}
                  />
                  <TextField
                    size="small"
                    label="To"
                    type="date"
                    value={dateRange.end?.toISOString().split('T')[0] || ''}
                    onChange={(e) => setDateRange(prev => ({
                      ...prev,
                      end: e.target.value ? new Date(e.target.value) : null
                    }))}
                    InputLabelProps={{ shrink: true, ...harmonateInputLabelProps }}
                    sx={{ width: '160px', ...harmonateTextFieldSx }}
                  />
                </Box>
              </Box>

              {customFilters.length > 0 && (
                <Box display="flex" flexDirection="column" gap={2} sx={harmonateFormFieldsSx}>
                  {customFilters.map((filter, index) => (
                    <Card key={filter.id} variant="outlined" sx={{ borderColor: ht.line }}>
                      <CardContent sx={{ py: 2 }}>
                        <Grid container spacing={2} alignItems="center">
                          <Grid item xs={12} sm={3}>
                            <FormControl fullWidth size="small">
                              <InputLabel>Column</InputLabel>
                              <Select
                                value={filter.column}
                                onChange={(e) => updateCustomFilter(filter.id, 'column', e.target.value)}
                                label="Column"
                                MenuProps={harmonateSelectMenuProps}
                              >
                                {getAvailableColumns().map((col) => (
                                  <MenuItem key={col.value} value={col.value}>
                                    {col.label}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid>
                          
                          <Grid item xs={12} sm={3}>
                            <FormControl fullWidth size="small">
                              <InputLabel>Operator</InputLabel>
                              <Select
                                value={filter.operator}
                                onChange={(e) => updateCustomFilter(filter.id, 'operator', e.target.value)}
                                label="Operator"
                                MenuProps={harmonateSelectMenuProps}
                              >
                                {FILTER_OPERATORS.map((op) => (
                                  <MenuItem key={op.value} value={op.value}>
                                    {op.label}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid>
                          
                          <Grid item xs={12} sm={4}>
                            {(() => {
                              const selectedColumn = columns.find(col => col.field === filter.column);
                              const isDateColumn = selectedColumn?.type === 'date' || 
                                                 (selectedColumn?.field?.toLowerCase().includes('date') || 
                                                  selectedColumn?.field?.toLowerCase().includes('time'));
                              
                              // List of operators that don't require a value
                              const noValueOperators = ['isEmpty', 'isNotEmpty'];
                              const requiresValue = !noValueOperators.includes(filter.operator);
                              
                              if (!requiresValue) {
                                return null; // Don't render any input for these operators
                              }
                              
                              if (isDateColumn) {
                                return (
                                  <TextField
                                    fullWidth
                                    size="small"
                                    type="date"
                                    label="Date"
                                    value={filter.value || ''}
                                    onChange={(e) => updateCustomFilter(filter.id, 'value', e.target.value)}
                                    InputLabelProps={{
                                      shrink: true,
                                      ...harmonateInputLabelProps,
                                    }}
                                    sx={harmonateTextFieldSx}
                                  />
                                );
                              }
                              
                              return (
                                <TextField
                                  fullWidth
                                  size="small"
                                  label="Value"
                                  value={filter.value || ''}
                                  onChange={(e) => updateCustomFilter(filter.id, 'value', e.target.value)}
                                  placeholder="Enter filter value"
                                  sx={harmonateTextFieldSx}
                                />
                              );
                            })()}
                          </Grid>
                          
                          <Grid item xs={12} sm={2}>
                            <Box display="flex" justifyContent="center">
                              <IconButton
                                onClick={() => removeCustomFilter(filter.id)}
                                size="small"
                                sx={{
                                  ...harmonateGridActionIconSx,
                                  '&:hover': { color: ht.red, bgcolor: 'rgba(239, 63, 63, 0.08)' },
                                }}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Box>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              )}
            </Paper>
          )}

          <Paper elevation={0} sx={{ ...harmonateCardSx, bgcolor: ht.white, p: 2, mb: 2, flexShrink: 0 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body1" sx={{ color: ht.text }}>
                {loading ? 'Loading...' : `Page ${currentPage} (${transactions.length} records)`}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button 
                  variant="outlined" 
                  size="small"
                  disabled={loading || currentPage <= 1}
                  onClick={handlePreviousPage}
                  startIcon={<ArrowBackIcon />}
                  sx={harmonateUploadOutlinedSx}
                >
                  Previous
                </Button>
                <Button 
                  variant="contained" 
                  size="small"
                  disabled={loading}
                  onClick={handleLoadData}
                  sx={harmonateProceedButtonSx}
                >
                  Load Data
                </Button>
                <Button 
                  variant="outlined" 
                  size="small"
                  disabled={loading || !hasMore}
                  onClick={handleNextPage}
                  endIcon={<ArrowForwardIcon />}
                  sx={harmonateUploadOutlinedSx}
                >
                  Next
                </Button>
              </Box>
            </Box>
          </Paper>

          <Paper elevation={0} sx={{ ...harmonateCardSx, bgcolor: ht.white, p: 2, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
                <CircularProgress sx={{ color: ht.green }} />
              </Box>
            ) : (
              <div style={{ height: 600, width: '100%' }}>
                <DataGridPro
                  columnResizeMode="onEnd"
                  resizable
                  ref={(ref) => {
                    gridRef.current = ref;
                    if (ref) setApiRef(ref);
                  }}
                  rows={filteredTransactions}
                  columns={columns}
                  disableSelectionOnClick
                  onSortModelChange={(model) => setSortModel(model)}
                  sortModel={sortModel}
                  slots={{
                    toolbar: () => (
                      <GridToolbarContainer sx={{
                        '& .MuiButton-root': { color: ht.text, textTransform: 'none' },
                        '& .MuiSvgIcon-root': { color: ht.text },
                      }}>
                        <GridToolbarColumnsButton />
                        <GridToolbarFilterButton />
                        <GridToolbarDensitySelector />
                        {canExport && (
                          <>
                        <Button 
                          startIcon={<GetAppIcon />}
                          onClick={() => {
                            const filename = `transactions_${new Date().toISOString().split('T')[0]}`;
                            exportToCsv(filteredTransactions, filename);
                          }}
                          sx={{ color: ht.text, textTransform: 'none' }}
                        >
                          Download as CSV
                        </Button>
                        <Button 
                          startIcon={<GetAppIcon />}
                          onClick={handleExcelExport}
                          sx={{ ml: 1, color: ht.text, textTransform: 'none' }}
                        >
                          Download as Excel
                        </Button>
                          </>
                        )}
                      </GridToolbarContainer>
                    ),
                  }}
                  apiRef={apiRef}
                  pagination
                  paginationMode="server"
                  paginationModel={paginationModel}
                  onPaginationModelChange={setPaginationModel}
                  rowCount={rowCountProp}
                  pageSizeOptions={[10, 25, 50, 100]}
                  disableColumnMenu={false}
                  disableColumnFilter={false}
                  disableColumnSelector={false}
                  sx={{
                    ...harmonateDataGridSx,
                    '& .MuiDataGrid-row.Mui-selected': {
                      bgcolor: ht.secondaryTab,
                      '&:hover': {
                        bgcolor: ht.secondaryTab,
                      },
                    },
                  }}
                />
              </div>
            )}
          </Paper>
        </Box>
      </Box>
      
      {/* File Upload Dialog */}
      <Dialog open={uploadDialogOpen} onClose={() => setUploadDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={harmonateDialogPaperProps}>
        <DialogTitle sx={harmonateDialogTitleSx}>Upload Credit Card Statement</DialogTitle>
        <DialogContent sx={harmonateDialogFormContentSx}>
          <Box sx={{ textAlign: 'center', ...harmonateFormFieldsSx }}>
            {isUploading ? (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <CircularProgress sx={{ mb: 2, color: ht.green }} />
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 500, color: ht.text }}>
                  {currentStep || 'Uploading...'}
                </Typography>
                <Typography variant="body2" sx={{ color: ht.fadedText }}>
                  {Math.round(uploadProgress)}% complete
                </Typography>
              </Box>
            ) : (
              <>
                <Box 
                  onClick={() => document.getElementById('file-upload').click()}
                  sx={{ 
                    textAlign: 'center',
                    cursor: 'pointer',
                    p: 3,
                    border: `2px dashed ${ht.line}`,
                    borderRadius: '8px',
                    backgroundColor: ht.listBg,
                    '&:hover': {
                      borderColor: ht.green,
                      backgroundColor: ht.white
                    }
                  }}
                >
                  <Box
                    sx={{
                      width: 60,
                      height: 60,
                      borderRadius: '50%',
                      backgroundColor: ht.secondaryTab,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mx: 'auto',
                      mb: 2
                    }}
                  >
                    <CloudUploadIcon sx={{ fontSize: 30, color: ht.green }} />
                  </Box>
                  
                  <Typography variant="body1" sx={{ mb: 1, color: ht.text, fontSize: '14px' }}>
                    Drag your file here or{' '}
                    <Typography 
                      component="span" 
                      sx={{ 
                        color: ht.green, 
                        fontWeight: 500,
                        '&:hover': { textDecoration: 'underline' }
                      }}
                    >
                      browse
                    </Typography>
                  </Typography>
                  
                  <Typography variant="body2" sx={{ color: ht.fadedText, fontSize: '12px', mb: 2 }}>
                    Supported formats: .csv, .xls, .xlsx (Max 10MB)
                  </Typography>
                  
                  <input
                    id="file-upload"
                    type="file"
                    accept=".csv,.xlsx,.xls"
                    onChange={handleFileUpload}
                    style={{ display: 'none' }}
                  />
                  
                  {uploadedFile && (
                    <Box sx={{ mt: 2, p: 2, border: `1px solid ${ht.line}`, borderRadius: 1, display: 'flex', alignItems: 'center', bgcolor: ht.white }}>
                      <InsertDriveFileIcon sx={{ mr: 1, color: ht.text }} />
                      <Typography variant="body2" sx={{ flexGrow: 1, color: ht.text }}>
                        {uploadedFile.name}
                      </Typography>
                      <Typography variant="caption" sx={{ color: ht.fadedText, mr: 1 }}>
                        {(uploadedFile.size / 1024 / 1024).toFixed(2)} MB
                      </Typography>
                      <IconButton size="small" onClick={() => setUploadedFile(null)} sx={harmonateGridActionIconSx}>
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  )}
                </Box>
                
                <FormControl fullWidth sx={{ mt: 3, ...harmonateTextFieldSx }} variant="outlined">
                  <InputLabel>Document Type</InputLabel>
                  <Select
                    value={selectedDocumentType}
                    label="Document Type"
                    onChange={(e) => setSelectedDocumentType(e.target.value)}
                    disabled={loadingDocumentTypes}
                    MenuProps={harmonateSelectMenuProps}
                  >
                    {loadingDocumentTypes ? (
                      <MenuItem value="">Loading...</MenuItem>
                    ) : (
                      documentTypes.map((type) => (
                        <MenuItem key={type.id} value={type.id}>
                          {type.name}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
                
                {uploadError && (
                  <Alert severity="error" sx={{ mt: 2 }}>
                    {uploadError}
                  </Alert>
                )}
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setUploadDialogOpen(false)} disabled={isUploading} variant="outlined" sx={harmonateCancelButtonSx}>
            Cancel
          </Button>
          <Button 
            onClick={handleUpload} 
            variant="contained"
            disabled={!uploadedFile || !selectedDocumentType || isUploading}
            startIcon={isUploading ? <CircularProgress size={20} color="inherit" /> : <CloudUploadIcon />}
            sx={harmonateProceedButtonSx}
          >
            {isUploading ? 'Uploading...' : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Save Filter Dialog */}
      <Dialog 
        open={saveFilterDialogOpen} 
        onClose={() => {
          setSaveFilterDialogOpen(false);
          setFilterName('');
          setSelectedFilter('');
        }} 
        maxWidth="sm" 
        fullWidth
        PaperProps={harmonateDialogPaperProps}
      >
        <DialogTitle sx={harmonateDialogTitleSx}>{selectedFilter ? 'Update Filter' : 'Save Current Filter'}</DialogTitle>
        <DialogContent sx={{ ...harmonateDialogFormContentSx, ...harmonateFormFieldsSx }}>
          <TextField
            autoFocus
            margin="dense"
            label="Filter Name"
            type="text"
            fullWidth
            value={filterName}
            onChange={(e) => setFilterName(e.target.value)}
            disabled={savingFilter}
            InputLabelProps={harmonateInputLabelProps}
            sx={harmonateTextFieldSx}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button 
            onClick={() => {
              setSaveFilterDialogOpen(false);
              setFilterName('');
              setSelectedFilter('');
            }} 
            disabled={savingFilter}
            variant="outlined"
            sx={harmonateCancelButtonSx}
          >
            Cancel
          </Button>
          {selectedFilter ? (
            <>
              <Button 
                onClick={() => handleSaveFilter(true)} 
                variant="contained"
                disabled={!filterName.trim() || savingFilter}
                startIcon={savingFilter ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                sx={harmonateProceedButtonSx}
              >
                {savingFilter ? 'Updating...' : 'Update'}
              </Button>
              <Button 
                onClick={() => {
                  setSelectedFilter('');
                  handleSaveFilter(false);
                }} 
                variant="outlined"
                disabled={!filterName.trim() || savingFilter}
                startIcon={<SaveAsIcon />}
                sx={harmonateUploadOutlinedSx}
              >
                Save as New
              </Button>
            </>
          ) : (
            <Button 
              onClick={() => handleSaveFilter(false)} 
              variant="contained"
              disabled={!filterName.trim() || savingFilter}
              startIcon={savingFilter ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
              sx={harmonateProceedButtonSx}
            >
              {savingFilter ? 'Saving...' : 'Save'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
      
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({...snackbar, open: false})}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbar({...snackbar, open: false})} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default CreditCardTransactions;